import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { PageHeader, Btn, Input, Select, Textarea, Modal, Badge, Card, EmptyState, fmtMoney, fmtDate } from "@/components/ui-kit";
import { Plus, Search, FileSpreadsheet, FileText as FilePdf, Eye, Trash2, Upload } from "lucide-react";
import BulkImportModal from "@/components/BulkImportModal";

const CLASSES = ["A+", "A", "B", "C"];
const emptyP = {
  name: "", branch: "", owner_name: "", branch_manager: "", pharmacist_names: "",
  mobile: "", whatsapp: "", landline: "", governorate: "", city: "", area: "", address: "",
  latitude: "", longitude: "", otc_movement_level: "", supplement_movement: "",
  estimated_daily_sales: "", customer_type: "", nearby_competitors: "",
  classification: "B", purchasing_decision_maker: "", recommendation_influencer: "",
  pushing_pharmacist: "", payment_policy: "", credit_limit: "", collection_days: 30,
  preferred_bonus: "", preferred_offers: "", fast_moving_products: "", slow_moving_products: "",
  main_competing_brands: "", competitor_pricing: "", competitor_offers: "",
  shelf_presence: "", stand_presence: "", notes: "",
};

export default function Pharmacies() {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState({ governorate: "", classification: "" });
  const [openForm, setOpenForm] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyP);
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (filter.governorate) params.governorate = filter.governorate;
    if (filter.classification) params.classification = filter.classification;
    const { data } = await api.get("/pharmacies", { params });
    setList(data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const tm = setTimeout(load, 300); return () => clearTimeout(tm); }, [q, filter]);

  const startCreate = () => { setEditing(null); setForm(emptyP); setOpenForm(true); };
  const startEdit = (d) => { setEditing(d.id); setForm({ ...emptyP, ...d }); setOpenForm(true); };
  const view = async (id) => { const { data } = await api.get(`/pharmacies/${id}`); setViewing(data); setOpenView(true); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      ["latitude", "longitude", "estimated_daily_sales", "credit_limit", "collection_days"].forEach(k => {
        if (payload[k] === "") payload[k] = null;
        else if (payload[k] != null) payload[k] = Number(payload[k]);
      });
      if (editing) await api.put(`/pharmacies/${editing}`, payload);
      else await api.post("/pharmacies", payload);
      setOpenForm(false);
      await load();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/pharmacies/${id}`); await load();
  };

  const classColor = (c) => c === "A+" ? "blue" : c === "A" ? "green" : c === "B" ? "amber" : "slate";
  const otcColor = (l) => l === "high" ? "green" : l === "medium" ? "amber" : "slate";

  return (
    <div data-testid="pharmacies-page">
      <PageHeader
        title={t("pharmacies")}
        subtitle="Pharmacy intelligence & commercial relationships"
        actions={
          <>
            <Btn variant="secondary" onClick={() => setBulkOpen(true)} data-testid="bulk-import-pharmacies"><Upload className="w-4 h-4"/>Import</Btn>
            <Btn variant="secondary" onClick={() => window.open(`${API_BASE}/export/pharmacies/excel`)}><FileSpreadsheet className="w-4 h-4"/>Excel</Btn>
            <Btn variant="secondary" onClick={() => window.open(`${API_BASE}/export/pharmacies/pdf`)}><FilePdf className="w-4 h-4"/>PDF</Btn>
            <Btn onClick={startCreate} data-testid="add-pharmacy-btn"><Plus className="w-4 h-4"/>{t("add")}</Btn>
          </>
        }
      />
      <Card className="p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="relative sm:col-span-2">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")+"..."} data-testid="pharmacies-search" className="block w-full rounded-md border-0 py-1.5 ps-9 pe-3 ring-1 ring-slate-300 focus:ring-2 focus:ring-blue-600 sm:text-sm bg-white"/>
          </div>
          <Select value={filter.governorate} onChange={(e) => setFilter({...filter, governorate: e.target.value})}>
            <option value="">{t("governorate")}</option><option>Gharbia</option><option>Kafr El Sheikh</option>
          </Select>
          <Select value={filter.classification} onChange={(e) => setFilter({...filter, classification: e.target.value})}>
            <option value="">{t("classification")}</option>{CLASSES.map(c => <option key={c}>{c}</option>)}
          </Select>
        </div>
      </Card>
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-y border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
                <th className="py-2 px-3 text-start">{t("pharmacyName")}</th>
                <th className="py-2 px-3 text-start">{t("classification")}</th>
                <th className="py-2 px-3 text-start">{t("city")}</th>
                <th className="py-2 px-3 text-start">{t("otcLevel")}</th>
                <th className="py-2 px-3 text-start">{t("owner")}</th>
                <th className="py-2 px-3 text-end">Outstanding</th>
                <th className="py-2 px-3 text-end">Total Orders</th>
                <th className="py-2 px-3 text-end">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-3">
                    <div className="font-semibold text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.branch} · {p.area}</div>
                  </td>
                  <td className="py-2 px-3"><Badge color={classColor(p.classification)}>{p.classification}</Badge></td>
                  <td className="py-2 px-3 text-slate-700">{p.city || "-"}</td>
                  <td className="py-2 px-3"><Badge color={otcColor(p.otc_movement_level)}>{p.otc_movement_level || "-"}</Badge></td>
                  <td className="py-2 px-3 text-slate-700">{p.owner_name || "-"}</td>
                  <td className="py-2 px-3 text-end font-mono text-amber-700">{fmtMoney(p.outstanding_balance)}</td>
                  <td className="py-2 px-3 text-end font-mono text-blue-700">{fmtMoney(p.total_orders)}</td>
                  <td className="py-2 px-3 text-end">
                    <div className="inline-flex gap-1">
                      <button onClick={() => view(p.id)} className="p-1.5 rounded hover:bg-slate-100"><Eye className="w-4 h-4 text-slate-600"/></button>
                      <button onClick={() => startEdit(p)} className="text-blue-700 px-2 text-xs font-semibold hover:bg-blue-50 rounded">{t("edit")}</button>
                      <button onClick={() => remove(p.id)} className="p-1.5 rounded hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-600"/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.length === 0 && <EmptyState message={t("noData")} />}
        </div>
      </Card>

      <Modal open={openForm} onClose={() => setOpenForm(false)} title={editing ? "Edit Pharmacy" : "Add Pharmacy"} maxWidth="max-w-4xl">
        <form onSubmit={submit} className="space-y-4" data-testid="pharmacy-form">
          <div className="overline">Basic</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label={t("pharmacyName") + " *"} required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="pharmacy-name"/>
            <Input label={t("branch")} value={form.branch} onChange={(e) => setForm({...form, branch: e.target.value})}/>
            <Select label={t("classification")} value={form.classification} onChange={(e) => setForm({...form, classification: e.target.value})}>
              {CLASSES.map(c => <option key={c}>{c}</option>)}
            </Select>
            <Input label={t("owner")} value={form.owner_name} onChange={(e) => setForm({...form, owner_name: e.target.value})}/>
            <Input label="Branch Manager" value={form.branch_manager} onChange={(e) => setForm({...form, branch_manager: e.target.value})}/>
            <Input label="Pharmacist Names" value={form.pharmacist_names} onChange={(e) => setForm({...form, pharmacist_names: e.target.value})}/>
            <Input label={t("mobile")} value={form.mobile} onChange={(e) => setForm({...form, mobile: e.target.value})}/>
            <Input label="WhatsApp" value={form.whatsapp} onChange={(e) => setForm({...form, whatsapp: e.target.value})}/>
            <Input label="Landline" value={form.landline} onChange={(e) => setForm({...form, landline: e.target.value})}/>
          </div>
          <div className="overline pt-2">Location</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Select label={t("governorate")} value={form.governorate} onChange={(e) => setForm({...form, governorate: e.target.value})}>
              <option value="">--</option><option>Gharbia</option><option>Kafr El Sheikh</option>
            </Select>
            <Input label={t("city")} value={form.city} onChange={(e) => setForm({...form, city: e.target.value})}/>
            <Input label={t("area")} value={form.area} onChange={(e) => setForm({...form, area: e.target.value})}/>
            <Input label="Address" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="md:col-span-3"/>
            <Input label="Latitude" type="number" step="any" value={form.latitude} onChange={(e) => setForm({...form, latitude: e.target.value})}/>
            <Input label="Longitude" type="number" step="any" value={form.longitude} onChange={(e) => setForm({...form, longitude: e.target.value})}/>
          </div>
          <div className="overline pt-2">Business & Commercial</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Select label={t("otcLevel")} value={form.otc_movement_level} onChange={(e) => setForm({...form, otc_movement_level: e.target.value})}>
              <option value="">--</option><option>high</option><option>medium</option><option>low</option>
            </Select>
            <Input label="Supplement Movement" value={form.supplement_movement} onChange={(e) => setForm({...form, supplement_movement: e.target.value})}/>
            <Input label="Daily Sales (EGP)" type="number" value={form.estimated_daily_sales} onChange={(e) => setForm({...form, estimated_daily_sales: e.target.value})}/>
            <Input label="Customer Type" value={form.customer_type} onChange={(e) => setForm({...form, customer_type: e.target.value})}/>
            <Input label="Credit Limit" type="number" value={form.credit_limit} onChange={(e) => setForm({...form, credit_limit: e.target.value})}/>
            <Input label="Collection Days" type="number" value={form.collection_days} onChange={(e) => setForm({...form, collection_days: e.target.value})}/>
            <Input label="Payment Policy" value={form.payment_policy} onChange={(e) => setForm({...form, payment_policy: e.target.value})}/>
            <Input label="Preferred Bonus" value={form.preferred_bonus} onChange={(e) => setForm({...form, preferred_bonus: e.target.value})}/>
            <Input label="Preferred Offers" value={form.preferred_offers} onChange={(e) => setForm({...form, preferred_offers: e.target.value})}/>
          </div>
          <div className="overline pt-2">Decision Makers</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label="Purchasing Decision" value={form.purchasing_decision_maker} onChange={(e) => setForm({...form, purchasing_decision_maker: e.target.value})}/>
            <Input label="Recommendation Influencer" value={form.recommendation_influencer} onChange={(e) => setForm({...form, recommendation_influencer: e.target.value})}/>
            <Input label="Pushing Pharmacist" value={form.pushing_pharmacist} onChange={(e) => setForm({...form, pushing_pharmacist: e.target.value})}/>
          </div>
          <div className="overline pt-2">Competitor Intel</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Main Competing Brands" value={form.main_competing_brands} onChange={(e) => setForm({...form, main_competing_brands: e.target.value})}/>
            <Input label="Competitor Pricing" value={form.competitor_pricing} onChange={(e) => setForm({...form, competitor_pricing: e.target.value})}/>
            <Input label="Competitor Offers" value={form.competitor_offers} onChange={(e) => setForm({...form, competitor_offers: e.target.value})}/>
            <Input label="Shelf Presence" value={form.shelf_presence} onChange={(e) => setForm({...form, shelf_presence: e.target.value})}/>
            <Input label="Fast Moving" value={form.fast_moving_products} onChange={(e) => setForm({...form, fast_moving_products: e.target.value})}/>
            <Input label="Slow Moving" value={form.slow_moving_products} onChange={(e) => setForm({...form, slow_moving_products: e.target.value})}/>
          </div>
          <Textarea label={t("notes")} rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <Btn variant="ghost" type="button" onClick={() => setOpenForm(false)}>{t("cancel")}</Btn>
            <Btn type="submit" disabled={busy} data-testid="pharmacy-submit">{busy ? t("loading") : t("save")}</Btn>
          </div>
        </form>
      </Modal>

      <Modal open={openView} onClose={() => setOpenView(false)} title="Pharmacy Details" maxWidth="max-w-3xl">
        {viewing && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>{viewing.pharmacy.name}</h3>
                <p className="text-sm text-slate-500">{viewing.pharmacy.owner_name} · {viewing.pharmacy.city}</p>
              </div>
              <Badge color={classColor(viewing.pharmacy.classification)}>{viewing.pharmacy.classification}</Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div><div className="text-xs text-slate-500 uppercase">Outstanding</div><div className="font-bold text-amber-700">{fmtMoney(viewing.pharmacy.outstanding_balance)} EGP</div></div>
              <div><div className="text-xs text-slate-500 uppercase">Total Orders</div><div className="font-bold text-blue-700">{fmtMoney(viewing.pharmacy.total_orders)} EGP</div></div>
              <div><div className="text-xs text-slate-500 uppercase">Visits</div><div className="font-bold">{viewing.pharmacy.visit_count}</div></div>
              <div><div className="text-xs text-slate-500 uppercase">Mobile</div><div className="font-semibold">{viewing.pharmacy.mobile || "-"}</div></div>
              <div><div className="text-xs text-slate-500 uppercase">Credit Limit</div><div className="font-semibold">{fmtMoney(viewing.pharmacy.credit_limit)} EGP</div></div>
              <div><div className="text-xs text-slate-500 uppercase">OTC Level</div><div className="font-semibold">{viewing.pharmacy.otc_movement_level || "-"}</div></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="overline mb-2">Recent Orders</div>
                <div className="space-y-1 text-xs">
                  {viewing.orders.slice(0,5).map(o => (
                    <div key={o.id} className="flex justify-between border-b border-slate-100 py-1">
                      <span className="font-mono">{o.invoice_number}</span>
                      <span className="font-bold">{fmtMoney(o.total)}</span>
                    </div>
                  ))}
                  {viewing.orders.length === 0 && <div className="text-slate-400">None</div>}
                </div>
              </div>
              <div>
                <div className="overline mb-2">Recent Collections</div>
                <div className="space-y-1 text-xs">
                  {viewing.collections.slice(0,5).map(c => (
                    <div key={c.id} className="flex justify-between border-b border-slate-100 py-1">
                      <span>{c.payment_method}</span>
                      <span className="font-bold text-emerald-700">{fmtMoney(c.amount)}</span>
                    </div>
                  ))}
                  {viewing.collections.length === 0 && <div className="text-slate-400">None</div>}
                </div>
              </div>
              <div>
                <div className="overline mb-2">Recent Visits</div>
                <div className="space-y-1 text-xs">
                  {viewing.visits.slice(0,5).map(v => (
                    <div key={v.id} className="flex justify-between border-b border-slate-100 py-1">
                      <span>{v.rep_name}</span>
                      <span>{fmtDate(v.created_at)}</span>
                    </div>
                  ))}
                  {viewing.visits.length === 0 && <div className="text-slate-400">None</div>}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <BulkImportModal entity="pharmacies" open={bulkOpen} onClose={() => setBulkOpen(false)} onImported={load} />
    </div>
  );
}
